import { StandardEvents } from '@shopify/events';

/**
 * Kaufweise Multi-Rabatt Block (Produktseite).
 *
 * Faelle:
 * 1. <kw-multirabatt> = der ganze Block. Hoert auf dasselbe StandardEvents.productSelect,
 *    das Horizons eigenes assets/product-price.js fuer den Preis nutzt (Variantenwechsel
 *    refetcht die ganze Section server-seitig), und uebernimmt bei jedem Variantenwechsel den
 *    INHALT des gleichnamigen, frisch gerenderten Elements aus der Antwort (nicht sich selbst
 *    ersetzen -> der Host bleibt derselbe DOM-Knoten, der Event-Listener bleibt aktiv). Dadurch
 *    bleiben Rundung, Grundpreis-Skalierung und Bestandsfilterung IMMER server-seitig in Liquid
 *    berechnet, keine zweite (JS-)Implementierung dieser Logik noetig (Fixrunde 18.09.2026,
 *    P1-2). Liefert eine Variante keinen Block (z. B. Setting inzwischen deaktiviert), wird der
 *    Block nur AUSGEBLENDET (`hidden`), nicht aus dem DOM entfernt (Restrunde 18.09.2026,
 *    Opus-Re-Audit 9.5 Punkt 1) - sonst wuerde `disconnectedCallback` den Listener auf der
 *    Section abmelden und der Block bliebe bei einem Rueckwechsel auf eine Variante MIT Block
 *    dauerhaft weg, bis die Seite neu laedt.
 * 2. <kw-multirabatt-tiles> = die Kachelreihe. Klick setzt die Menge in der bestehenden
 *    Horizon-Quantity-Komponente (quantity-selector-component), aktive Kachel folgt dem
 *    theme-eigenen "quantity-selector:update"-Event. Kein Fremd-JS/-Bibliothek.
 */
class KwMultirabattBlock extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    this._section = closestSection;
    this._onProductSelect = this.#handleProductSelect.bind(this);
    closestSection.addEventListener(StandardEvents.productSelect, this._onProductSelect);
  }

  disconnectedCallback() {
    if (this._section) {
      this._section.removeEventListener(StandardEvents.productSelect, this._onProductSelect);
    }
  }

  /**
   * @param {CustomEvent} event
   */
  #handleProductSelect(event) {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const promise = event.promise;
    if (!promise) return;

    promise
      .then(({ detail }) => {
        if (!detail || !detail.html) return;
        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
          return;
        }

        const newBlock = html.querySelector(`kw-multirabatt[data-block-id="${this.dataset.blockId}"]`);
        if (newBlock) {
          // Inhalt und Attribute des Hosts aktualisieren, den Knoten selbst (und damit den
          // Event-Listener auf der Section) am Leben lassen.
          this.replaceChildren(...newBlock.childNodes);
          for (const attr of Array.from(this.attributes)) {
            if (attr.name !== 'data-block-id') this.removeAttribute(attr.name);
          }
          for (const attr of Array.from(newBlock.attributes)) {
            this.setAttribute(attr.name, attr.value);
          }
          this.hidden = false;
        } else {
          // Kein neuer Block gefunden (z. B. Setting inzwischen deaktiviert, oder Variante ohne
          // gueltigen Preis) -> nur AUSBLENDEN, nicht entfernen (siehe Klassenkommentar). Beim
          // naechsten Variantenwechsel MIT Block wird oben wieder eingeblendet.
          this.hidden = true;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[kw-multirabatt] Event promise rejected:', error);
      });
  }
}

class KwMultirabattTiles extends HTMLElement {
  connectedCallback() {
    this.tiles = Array.from(this.querySelectorAll('[data-kw-multirabatt-tile]'));
    this.tiles.forEach((tile) => {
      tile.addEventListener('click', () => this.selectTile(tile));
    });
    this._onUpdate = (event) => this.syncActive(event.detail && event.detail.quantity);
    document.addEventListener('quantity-selector:update', this._onUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('quantity-selector:update', this._onUpdate);
  }

  /**
   * Findet die Quantity-Komponente im selben PDP-Kaufbereich.
   * @returns {HTMLElement|null}
   */
  getQuantitySelector() {
    const scope = this.closest('.shopify-section') || document;
    return scope.querySelector('quantity-selector-component[ref="quantitySelector"]');
  }

  /**
   * @param {HTMLElement} tile
   */
  selectTile(tile) {
    const qty = parseInt(tile.dataset.quantity, 10);
    if (!qty) return;

    const selector = this.getQuantitySelector();
    if (!selector) return;

    if (typeof selector.setValue === 'function') {
      selector.setValue(String(qty));
    } else {
      const input = selector.querySelector('input[name="quantity"]');
      if (input) input.value = String(qty);
    }

    if (typeof selector.updateButtonStates === 'function') {
      selector.updateButtonStates();
    }

    if (typeof selector.onQuantityChange === 'function') {
      selector.onQuantityChange();
    } else {
      selector.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.syncActive(qty);
  }

  /**
   * @param {number} qty
   */
  syncActive(qty) {
    if (!qty) return;
    this.tiles.forEach((tile) => {
      const tileQty = parseInt(tile.dataset.quantity, 10);
      const isFrom = tile.hasAttribute('data-from');
      const active = isFrom ? qty >= tileQty : qty === tileQty;
      tile.classList.toggle('is-active', active);
      tile.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }
}

if (!customElements.get('kw-multirabatt')) {
  customElements.define('kw-multirabatt', KwMultirabattBlock);
}
if (!customElements.get('kw-multirabatt-tiles')) {
  customElements.define('kw-multirabatt-tiles', KwMultirabattTiles);
}
